import Link from '@mui/icons-material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ModelError_Item, ModelError_Response } from '@treadinc/horizon-api-spec';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Site } from '~hooks/useSites/models';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompany } from '~utils/user/user-utils';

import { useSiteFormV2 } from './site-form-v2/hooks';
import { SiteFormV2, type SiteFormV2Handler } from './site-form-v2/SiteFormV2';
import {
  type SiteFormV2DirtyFields,
  SiteFormV2Fields,
  type SiteFormV2StateChangeProps,
  type SiteSavedResult,
} from './site-form-v2/types';

interface SiteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (result: SiteSavedResult) => void;
  site?: Site;
  siteId: string | null;
}

const SiteDialog = observer(
  ({ isOpen, onClose, onSuccess, site, siteId }: SiteDialogProps) => {
    const { toasterStore, userStore } = useStores();
    const { createNewSite, updateSite } = useSiteFormV2();
    const siteFormRef = useRef<SiteFormV2Handler>(null);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [isLoadingSiteData, setIsLoadingSiteData] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);
    const selectedCompany = getEffectiveUserCompany(userStore);

    const closeDialog = () => {
      onClose();
      setAsyncErrors([]);
    };

    const handleClose = () => {
      if (isSubmitting) {
        return;
      }

      closeDialog();
    };

    const handleSaveSuccess = (mode: SiteSavedResult['mode'], savedSite?: Site) => {
      const name = `${savedSite?.name}`;
      toasterStore.push(
        alert(
          mode === 'updated'
            ? t('administration.site.site_updated', { name })
            : t('administration.site.site_created', { name }),
          AlertTypes.success,
        ),
      );
      onSuccess?.({ mode });
      closeDialog();
    };

    const onSubmitCallback = async (
      data: SiteFormV2Fields,
      dirtyFields: SiteFormV2DirtyFields,
    ) => {
      setIsSubmitting(true);
      try {
        if (siteId) {
          const updatedSite = await updateSite({ id: siteId, ...data }, dirtyFields);
          handleSaveSuccess('updated', updatedSite);
        } else {
          const createdSite = await createNewSite(data);
          handleSaveSuccess('created', createdSite);
        }
      } catch (error) {
        const axiosError = error as AxiosError<ModelError_Response>;
        setAsyncErrors(axiosError.response?.data.error.errors || []);
      } finally {
        setIsSubmitting(false);
      }
    };

    const onSubmitForm = () => {
      siteFormRef.current?.submit(onSubmitCallback);
    };

    const onFormStateChange = ({
      isDirty,
      isValid,
      isLoadingSiteData,
    }: SiteFormV2StateChangeProps) => {
      setIsDirty(isDirty);
      setIsValid(!!isValid);
      setIsLoadingSiteData(isLoadingSiteData);
    };

    return (
      <Dialog
        isOpen={isOpen}
        maxWidth="xl"
        onClose={handleClose}
        preventCloseOnBackdropClick
      >
        <DialogHeader onClose={handleClose}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{t(`administration.site.${siteId ? 'update_site' : 'add_new'}`)}</span>

            {site?.companyShare && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                <Link sx={{ color: 'success.main' }} />
                <Typography
                  component="span"
                  sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                >
                  {t('administration.site.shared_from')}{' '}
                  {site.companyShare.senderCompany.legalName}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogHeader>

        <DialogContent>
          <SiteFormV2
            ref={siteFormRef}
            defaultSite={site}
            onFormStateChange={onFormStateChange}
            errors={asyncErrors}
            company={selectedCompany}
          />
        </DialogContent>

        <DialogActions>
          {site?.companyShare ? (
            <SmallButton color="secondary" onClick={handleClose} variant="outlined">
              {t('actions.close')}
            </SmallButton>
          ) : (
            <>
              <SmallButton
                color="secondary"
                disabled={isSubmitting}
                onClick={handleClose}
                variant="outlined"
              >
                {t('actions.cancel')}
              </SmallButton>

              <SmallLoadingButton
                color="primary"
                data-ui="page-settings-sites__site-form__submit-button"
                disabled={isSubmitting || !isDirty || !isValid || isLoadingSiteData}
                loading={isSubmitting}
                onClick={onSubmitForm}
              >
                {siteId ? t('actions.submit') : t('actions.create')}
              </SmallLoadingButton>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  },
);

export default SiteDialog;
